import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import logo from "../assets/logo.png";

const CONTRACT_VERSION = "matrixos-contract-v5";
const REGION = "us-central1";

const TERMS = [
  { title: "1. Parties", body: "This Client Services Agreement is entered into between MatrixOS (Pty) Ltd, registration number 2026 / 273497 / 07, trading as MatrixOS, and the client identified in this contract." },
  { title: "2. Services", body: "MatrixOS provides cloud-based software and related services, including Neo, Morpheus, Link, Super Admin, access layers, reporting, exports, onboarding, implementation support, integrations, and any other modules enabled for the client." },
  { title: "3. Trial & Go-Live", body: "The client may begin with trial, pilot, or demo access for the agreed period. Trial usage is not billed unless otherwise agreed. After trial, the client may choose which sites, stores, modules, or users continue into live use." },
  { title: "4. Monthly Usage Billing", body: "Billing may be calculated using enabled modules, live sites, stores, active staff, live sessions, dispatch usage, fixed monthly module charges, or other agreed pricing. Going live does not always trigger an immediate charge, but live usage and agreed monthly charges may become payable for the relevant billing period." },
  { title: "5. Payment Terms", body: "Payment is due according to the accepted invoice, checkout, proposal, order form, or signed agreement. Payment may be collected by EFT, invoice, Paystack, card, or another supported method." },
  { title: "6. Grace Period & Enforcement", body: "A grace period may apply where agreed. If payment remains outstanding, MatrixOS may restrict selected functions, pause integrations, suspend access, or limit support until the account is brought up to date." },
  { title: "7. Pricing Basis", body: "Pricing may scale by site, store, module, active staff, live usage, implementation scope, integration requirements, or agreed custom pricing. MatrixOS may update pricing on reasonable notice unless a signed agreement states otherwise." },
  { title: "8. Refunds", body: "Amounts already billed for live usage, active subscriptions, fixed monthly module charges, setup, onboarding, custom work, or integrations are generally non-refundable. Exceptional cases may be reviewed by MatrixOS at its discretion." },
  { title: "9. Termination", body: "The client may cancel according to the accepted commercial terms. Cancellation stops future billing from the effective cancellation date or next applicable billing cycle and does not remove amounts already due for prior live usage, fixed monthly charges, setup work, or other charges already incurred." },
  { title: "10. Client Responsibilities", body: "The client is responsible for lawful use, staff access control, device management, credential security, site permissions, user roles, data accuracy, internal policies, and decisions made using MatrixOS records or reports." },
  { title: "11. Data & Intellectual Property", body: "The client retains ownership of its business data. MatrixOS retains all right, title, and interest in the platform, software, workflows, designs, dashboards, reports, branding, documentation, and intellectual property." },
  { title: "12. Availability & Changes", body: "MatrixOS aims to provide reliable service but does not guarantee uninterrupted availability. Services may be updated, improved, renamed, reorganized, added to, limited, or modified as the platform evolves." },
  { title: "13. Integrations & Third Parties", body: "MatrixOS may connect to third-party POS, ecommerce, payment, email, authentication, hosting, or cloud services. MatrixOS is not responsible for third-party outages, incorrect source data, revoked access, API changes, integration limits, or provider delays outside its control." },
  { title: "14. Limitation of Liability", body: "To the fullest extent allowed by law, MatrixOS is not liable for indirect, consequential, special, or economic loss, including lost profit, lost revenue, downtime impact, stock losses, delivery delays, inaccurate source data, or data-entry errors, except where liability cannot legally be excluded." },
  { title: "15. POPIA & Confidentiality", body: "Each party must treat confidential information appropriately. Personal information must be handled in accordance with applicable South African law, including POPIA where relevant. The client confirms it has authority to provide staff, customer, driver, stock, POS, and operational data to MatrixOS." },
  { title: "16. Electronic Signature", body: "This agreement may be accepted and signed electronically. Electronic acceptance and signature are deemed legally binding and enforceable." },
  { title: "17. Governing Law", body: "This agreement is governed by the laws of the Republic of South Africa, and disputes are subject to South African courts unless a signed agreement says otherwise or the law requires otherwise." },
];

function Toast({ open, title, message, tone = "success", onClose }) {
  if (!open) return null;
  const toneClasses =
    tone === "success"
      ? "border-cyan-400/45 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.22),rgba(15,23,42,0.96))] shadow-[0_0_0_1px_rgba(34,211,238,0.12),0_18px_48px_rgba(6,182,212,0.28)]"
      : "border-rose-400/45 bg-[radial-gradient(circle_at_top,rgba(244,63,94,0.18),rgba(15,23,42,0.96))] shadow-[0_0_0_1px_rgba(244,63,94,0.12),0_18px_48px_rgba(244,63,94,0.18)]";


  return (
    <div className="fixed right-4 top-24 z-[9999] max-w-[calc(100vw-2rem)]">
      <div className={`w-[380px] rounded-2xl border p-4 backdrop-blur-xl ${toneClasses}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-100/90">{message}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-300 hover:text-white">✕</button>
        </div>
      </div>
    </div>
  );
}

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.max(wrap.clientWidth, 320);
    const height = 190;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = value;
    } else {
      ctx.clearRect(0, 0, width, height);
    }
  }, [value]);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    return { x: source.clientX - rect.left, y: source.clientY - rect.top };
  };

  const start = (event) => {
    event.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pointFromEvent(event);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pointFromEvent(event);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="space-y-3">
      <div ref={wrapRef} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <canvas
          ref={canvasRef}
          className="block w-full touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Draw signature using finger, stylus, or mouse.</p>
        <button type="button" onClick={clear} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Clear signature</button>
      </div>
    </div>
  );
}

function InvalidState({ reason }) {
  const map = {
    missing: "A secure contract token is required.",
    invalid: "This secure contract link is invalid.",
    expired: "This secure contract link has expired.",
    used: "This secure contract link has already been used.",
  };
  return (
    <div className="rounded-[2rem] border border-rose-400/20 bg-white p-8 text-slate-900 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
      <div className="text-xs uppercase tracking-[0.28em] text-rose-600">Contract unavailable</div>
      <h2 className="mt-3 text-3xl font-extrabold">This link cannot be used</h2>
      <p className="mt-3 leading-7 text-slate-600">{map[reason] || map.invalid}</p>
    </div>
  );
}

export default function ContractPage() {
  const fns = useMemo(() => getFunctions(undefined, REGION), []);
  const [form, setForm] = useState({ fullName: "", companyName: "", email: "", phone: "", notes: "", acceptedTerms: false, acceptedElectronic: false });
  const [meta, setMeta] = useState({ valid: false, reason: "", expiresAtLabel: "", productName: "Neo Platform", billingModel: "Per store", trialDays: 30 });
  const [token, setToken] = useState("");
  const [signature, setSignature] = useState("");
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [toast, setToast] = useState({ open: false, title: "", message: "", tone: "success" });
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!signed) return;
    setRedirecting(true);
    const timer = window.setTimeout(() => {
      window.location.assign("/login");
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [signed]);

  const canSubmit = useMemo(() => meta.valid && form.fullName.trim() && form.email.trim() && form.acceptedTerms && form.acceptedElectronic && !!signature && !submitting && !signed, [meta.valid, form, signature, submitting, signed]);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    let alive = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const secureToken = String(params.get("token") || "").trim();
      setToken(secureToken);
      if (!secureToken) {
        setMeta({ valid: false, reason: "missing", expiresAtLabel: "", productName: "Neo Platform", billingModel: "Per store", trialDays: 30 });
        setLoadingInvite(false);
        return;
      }
      try {
        const getInvite = httpsCallable(fns, "getClientContractInvite");
        const res = await getInvite({ token: secureToken });
        const payload = res?.data || {};
        if (!alive) return;
        setForm((prev) => ({ ...prev, fullName: payload.fullName || "", companyName: payload.companyName || "", email: payload.email || "" }));
        setMeta({ valid: true, reason: "", expiresAtLabel: payload.expiresAtLabel || "", productName: payload.productName || "Neo Platform", billingModel: payload.billingModel || "Per store", trialDays: Number(payload.trialDays || 30) });
      } catch (error) {
        if (!alive) return;
        const message = String(error?.message || "").toLowerCase();
        let reason = "invalid";
        if (message.includes("expired")) reason = "expired";
        if (message.includes("used")) reason = "used";
        setMeta({ valid: false, reason, expiresAtLabel: "", productName: "Neo Platform", billingModel: "Per store", trialDays: 30 });
      } finally {
        if (alive) setLoadingInvite(false);
      }
    })();
    return () => { alive = false; };
  }, [fns]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setToast({ open: true, title: "Missing information", message: "Please complete the required fields, accept the contract, and add the client signature.", tone: "error" });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    try {
      setSubmitting(true);
      const submit = httpsCallable(fns, "submitClientContract");
      await submit({ token, phone: form.phone.trim(), notes: form.notes.trim(), acceptedTerms: true, acceptedElectronic: true, signatureDataUrl: signature, pagePath: typeof window !== "undefined" ? window.location.pathname : "/contract" });
      setSigned(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setToast({ open: true, title: "Contract signed", message: "A signed copy will be emailed now. Please also check your email for the password setup link. Redirecting to login...", tone: "success" });
    } catch (error) {
      console.error("[ContractPage] submit failed", error);
      setToast({ open: true, title: "Could not submit", message: error?.message || "Something went wrong while saving the client contract.", tone: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#071120] text-white">
      <SiteHeader />
      <Toast open={toast.open} title={toast.title} message={toast.message} tone={toast.tone} onClose={() => setToast({ open: false, title: "", message: "", tone: "success" })} />
      <main className="mx-auto max-w-7xl px-6 pb-20 pt-32">
        <section className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr]">
          <div className="space-y-8">
            <div className="rounded-[2rem] border border-cyan-300/12 bg-[linear-gradient(180deg,rgba(19,49,90,0.56),rgba(8,20,37,0.96))] p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-cyan-400/10 ring-1 ring-cyan-300/20 shadow-[0_0_32px_rgba(34,211,238,0.24)]">
                    <img src={logo} alt="MatrixOS logo" className="h-16 w-16 object-contain" />
                  </div>
                  <div>
                    <div className="mb-2 inline-flex rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-200">Client contract</div>
                    <h1 className="text-4xl font-extrabold leading-tight text-white md:text-5xl">MatrixOS</h1>
                    <p className="mt-2 text-xl text-cyan-300">Operate with clarity</p>
                  </div>
                </div>
                <div className="grid gap-2 text-sm text-slate-300 sm:text-right">
                  <div>{meta.productName}</div>
                  <div>{meta.billingModel}</div>
                  <div>1 free trial store • billed monthly after live usage</div>
                </div>
              </div>
            </div>
            <div className="rounded-[2rem] border border-cyan-300/12 bg-[linear-gradient(180deg,rgba(18,36,60,0.96),rgba(8,20,37,0.98))] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.28em] text-cyan-300">Client services agreement</div>
                  <h2 className="mt-3 text-3xl font-extrabold text-white">Please review before signing</h2>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">Version: <span className="font-semibold text-white">{CONTRACT_VERSION}</span></div>
              </div>
              <div className="mt-6 max-h-[720px] space-y-4 overflow-y-auto pr-1">
                {TERMS.map((item) => (
                  <div key={item.title} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                    <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    <p className="mt-2 leading-7 text-slate-300">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="self-start rounded-[2rem] border border-cyan-300/12 bg-white p-8 text-slate-900 shadow-[0_30px_80px_rgba(0,0,0,0.35)] md:p-10">
            {loadingInvite ? (
              <div className="py-16 text-center"><div className="text-xs uppercase tracking-[0.28em] text-cyan-700">Secure access</div><h2 className="mt-3 text-3xl font-extrabold">Loading contract…</h2></div>
            ) : !meta.valid ? (
              <InvalidState reason={meta.reason} />
            ) : (
              <>
                <div className="mb-6">
                  <div className="text-xs uppercase tracking-[0.28em] text-cyan-700">Client acceptance</div>
                  <h2 className="mt-3 text-3xl font-extrabold">Accept and sign</h2>
                  <p className="mt-3 leading-7 text-slate-600">This secure contract link is valid until {meta.expiresAtLabel}. Review the contract, accept the terms, and sign digitally.</p>
                </div>
                {signed && (
                  <div className="mb-5 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-4 text-sm text-cyan-900">
                    Your signed copy will be emailed to you, and a password setup email has been sent for login access. You will be redirected to <strong>/login</strong> in about 10 seconds.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <Field label="Client full name *"><input className={inputCls} value={form.fullName} onChange={(e) => set("fullName", e.target.value)} placeholder="Name and surname" /></Field>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Company name"><input className={inputCls} value={form.companyName} onChange={(e) => set("companyName", e.target.value)} placeholder="Business or trading name" /></Field>
                    <Field label="Client email *"><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="client@company.com" /></Field>
                  </div>
                  <Field label="Phone / WhatsApp"><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" /></Field>
                  <Field label="Notes"><textarea className={`${inputCls} min-h-[110px]`} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional onboarding notes" /></Field>
                  <label className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4 text-sm text-slate-700"><input type="checkbox" checked={form.acceptedTerms} onChange={(e) => set("acceptedTerms", e.target.checked)} className="mt-1" /><span>I confirm that I have read and accept this client services agreement and all terms set out in it.</span></label>
                  <label className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4 text-sm text-slate-700"><input type="checkbox" checked={form.acceptedElectronic} onChange={(e) => set("acceptedElectronic", e.target.checked)} className="mt-1" /><span>I consent to electronic signature and electronic delivery of the signed contract.</span></label>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-700">Client signature *</div><SignaturePad value={signature} onChange={setSignature} /></div>
                  <button type="submit" disabled={!canSubmit} className="w-full rounded-full bg-cyan-400 px-6 py-3 font-semibold text-[#071120] transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50">{submitting ? "Submitting contract..." : signed ? (redirecting ? "Redirecting to login..." : "Contract signed") : "Accept & Sign"}</button>
                </form>
              </>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

const inputCls = "mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500";
function Field({ label, children }) { return (<div><label className="text-sm font-medium text-slate-700">{label}</label>{children}</div>); }
